/**
 * Health Monitor — Cross-connection health dashboard
 *
 * Shows: stat cards, circuit breaker status board, connection health grid,
 * latency trends chart, recent error log
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  Activity, Plug, AlertTriangle, Clock, RefreshCw,
  RotateCcw, CheckCircle, XCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StatusDot } from '@/components/shared/status-dot';
import { statusToDot } from '@/components/connections/connection-status-badge';
import { CB_BADGE_STYLES } from '@/components/connections/circuit-breaker-badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  TIER_LABELS,
  type ConnectionStatus,
} from '@/lib/mock-connections-data';
import {
  useMcpServers,
  useApiConnections,
  useConnectionErrors,
  useResetMcpCircuitBreaker,
} from '@/lib/use-connections-module';

export const Route = createFileRoute('/connections/health')({
  component: HealthMonitorPage,
});

// ── Types ──────────────────────────────────────────────────────────────────

type TimeRange = '1h' | '6h' | '24h' | '7d';

interface HealthItem {
  id: string;
  name: string;
  type: 'mcp' | 'api';
  status: ConnectionStatus;
  latencyMs: number | null;
  uptimePercent: number | null;
  errorRate: number | null;
  cbState?: string;
}

// Status helpers and CB badge styles imported from @/components/connections/

const SEVERITY_STYLES: Record<string, { bg: string; text: string; icon: typeof AlertTriangle }> = {
  warning: { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', icon: AlertTriangle },
  error: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', icon: XCircle },
  critical: { bg: 'bg-red-500/20', text: 'text-red-700 dark:text-red-300', icon: XCircle },
};

const LINE_COLORS = ['#8b5cf6', '#06b6d4', '#f59e0b', '#10b981', '#ef4444', '#ec4899'];

// ── Main Page ──────────────────────────────────────────────────────────────

function HealthMonitorPage() {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');

  // Real-data: MCP installs + API connections + recent error log
  // Bug #966 fix: Refresh must refetch ALL three panels, not just /errors.
  const { data: mcpServers = [], refetch: refetchMcp } = useMcpServers();
  const { data: apiConnections = [], refetch: refetchApi } = useApiConnections();
  const { data: errorLog = [], refetch: refetchErrors } = useConnectionErrors();

  // Combine all connections into health items
  const healthItems: HealthItem[] = useMemo(() => {
    const mcp: HealthItem[] = mcpServers.map(s => ({
      id: s.id, name: s.displayName, type: 'mcp' as const,
      status: s.status, latencyMs: s.metrics.latencyMs,
      uptimePercent: s.metrics.uptimePercent, errorRate: s.metrics.errorRate,
      cbState: s.circuitBreaker.state,
    }));
    const api: HealthItem[] = apiConnections.map(c => ({
      id: c.id, name: c.displayName, type: 'api' as const,
      status: c.status, latencyMs: c.metrics.latencyMs,
      uptimePercent: c.metrics.uptimePercent, errorRate: c.metrics.errorRate,
    }));
    return [...mcp, ...api];
  }, [mcpServers, apiConnections]);

  // Stats — guard against missing telemetry. If no connection has metrics
  // yet, show "—" instead of inventing zeros.
  const totalConnections = healthItems.length;
  const healthyCount = healthItems.filter(h => h.status === 'online').length;
  const itemsWithLatency = healthItems.filter(h => h.latencyMs != null);
  const itemsWithErrorRate = healthItems.filter(h => h.errorRate != null);
  const avgLatency = itemsWithLatency.length > 0
    ? Math.round(itemsWithLatency.reduce((sum, h) => sum + (h.latencyMs ?? 0), 0) / itemsWithLatency.length)
    : null;
  const avgErrorRate = itemsWithErrorRate.length > 0
    ? (itemsWithErrorRate.reduce((sum, h) => sum + (h.errorRate ?? 0), 0) / itemsWithErrorRate.length * 100).toFixed(2)
    : null;

  // Latency chart: only render when at least one connection has telemetry.
  const chartConnections = healthItems.filter(h => h.latencyMs != null).slice(0, 5);
  const chartData = useMemo(() => {
    if (chartConnections.length === 0) return [];
    const points = 12;
    return Array.from({ length: points }, (_, i) => {
      const ts = Date.now() - (points - i) * 5 * 60 * 1000;
      const row: Record<string, string | number> = {
        time: new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      };
      chartConnections.forEach(conn => {
        if (conn.latencyMs != null) row[conn.name] = conn.latencyMs;
      });
      return row;
    });
  }, [chartConnections]);

  // CB servers only
  const cbServers = mcpServers.filter(s => s.circuitBreaker.state !== 'closed' || s.circuitBreaker.failures > 0);

  // Real backend reset.
  const resetCircuitBreaker = useResetMcpCircuitBreaker();
  const handleResetCB = (id: string) => {
    const name = mcpServers.find(s => s.id === id)?.displayName ?? 'server';
    resetCircuitBreaker.mutate(id, {
      onSuccess: () => toast.success(`Circuit breaker reset for ${name}`),
      onError: (err) => toast.error(`Reset failed: ${err.message}`),
    });
  };

  return (
    <div className="p-6 max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Health Monitor</h2>
          <p className="text-sm text-muted-foreground">Real-time health status across all MCP servers and API connections.</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          data-testid="health-refresh"
          onClick={async () => {
            // Bug #966 fix: Refresh must refetch MCP + API + errors,
            // not just /errors. Honesty: don't toast "refreshed" until
            // all three panels have completed.
            const results = await Promise.allSettled([
              refetchMcp(),
              refetchApi(),
              refetchErrors(),
            ]);
            const failed = results.filter(r => r.status === 'rejected').length;
            if (failed === 0) {
              toast.success('Health data refreshed');
            } else {
              toast.error(`Health refresh partial: ${failed} of 3 panels failed`);
            }
          }}
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      {/* Top stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Plug} label="Total Connections" value={String(totalConnections)} />
        <StatCard icon={CheckCircle} label="Healthy" value={String(healthyCount)} trend={`${totalConnections - healthyCount} need attention`} trendUp={healthyCount === totalConnections} />
        <StatCard icon={Clock} label="Avg Latency" value={avgLatency != null ? `${avgLatency}ms` : '—'} />
        <StatCard icon={AlertTriangle} label="Avg Error Rate" value={avgErrorRate != null ? `${avgErrorRate}%` : '—'} trendUp={avgErrorRate != null && Number(avgErrorRate) < 1} />
      </div>

      {/* Circuit Breaker Status Board */}
      {cbServers.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Circuit Breaker Status</CardTitle>
            <CardDescription className="text-xs">MCP servers with active or recent circuit breaker events</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border">
              <div className="grid grid-cols-[1fr_80px_100px_80px_140px_80px] gap-3 p-3 bg-muted/50 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                <span>Server</span>
                <span>Tier</span>
                <span>State</span>
                <span>Failures</span>
                <span>Last Failure</span>
                <span>Action</span>
              </div>
              <div className="divide-y">
                {cbServers.map(server => (
                  <div key={server.id} className="grid grid-cols-[1fr_80px_100px_80px_140px_80px] gap-3 p-3 items-center hover:bg-muted/30 transition-colors">
                    <div className="flex items-center gap-2">
                      <StatusDot status={statusToDot(server.status)} size="sm" />
                      <span className="text-sm font-medium">{server.displayName}</span>
                    </div>
                    <Badge variant="outline" className="text-[9px] w-fit">{TIER_LABELS[server.tier].label.split(' — ')[0]}</Badge>
                    <Badge variant="outline" className={cn('text-[9px] w-fit', CB_BADGE_STYLES[server.circuitBreaker.state])}>
                      {server.circuitBreaker.state.replace('_', ' ')}
                    </Badge>
                    <span className="text-sm text-center">{server.circuitBreaker.failures}/{server.circuitBreaker.threshold}</span>
                    <span className="text-[11px] text-muted-foreground">
                      {server.circuitBreaker.lastFailure
                        ? new Date(server.circuitBreaker.lastFailure).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '—'}
                    </span>
                    <div>
                      {server.circuitBreaker.state !== 'closed' && (
                        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => handleResetCB(server.id)}>
                          <RotateCcw className="h-3 w-3" /> Reset
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Connection Health Grid */}
      <div>
        <h3 className="text-sm font-semibold mb-3">All Connections</h3>
        {totalConnections === 0 ? (
          <div className="border border-dashed rounded-lg p-8 flex flex-col items-center justify-center text-center">
            <Activity className="h-8 w-8 text-muted-foreground/30 mb-2" />
            <p className="text-sm font-medium text-muted-foreground">
              Health monitoring will be available once connections are registered
            </p>
            <p className="text-xs text-muted-foreground/70 mt-1 max-w-md">
              Register MCP servers or API connections to see live latency, uptime, error rates, and circuit breaker status here.
            </p>
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {healthItems.map(item => (
            <Card
              key={item.id}
              className="cursor-pointer hover:border-primary/30 transition-colors"
              onClick={() => navigate({ to: '/connections/$connectionId', params: { connectionId: item.id } })}
            >
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <StatusDot status={statusToDot(item.status)} size="sm" />
                    <span className="text-sm font-medium">{item.name}</span>
                  </div>
                  <Badge variant="outline" className="text-[9px]">
                    {item.type === 'mcp' ? 'MCP' : 'API'}
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-xs font-semibold">{item.latencyMs != null ? `${item.latencyMs}ms` : '—'}</p>
                    <p className="text-[10px] text-muted-foreground">Latency</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold">{item.uptimePercent != null ? `${item.uptimePercent}%` : '—'}</p>
                    <p className="text-[10px] text-muted-foreground">Uptime</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold">{item.errorRate != null ? `${(item.errorRate * 100).toFixed(1)}%` : '—'}</p>
                    <p className="text-[10px] text-muted-foreground">Errors</p>
                  </div>
                </div>
                {/* C-A11Y cluster (issue #727 CONN-HEALTH-010):
                    Don't render an unlabeled zero-value progress bar when
                    uptime telemetry is unavailable — it misleads sighted
                    users (looks like 0% uptime) and gives screen-reader
                    users an unlabeled progressbar element. When real data
                    is present, render the bar with an explicit
                    aria-label so AT users hear what it represents. */}
                {item.uptimePercent != null ? (
                  <Progress
                    value={item.uptimePercent}
                    className="h-1.5 mt-2"
                    aria-label={`${item.name} uptime: ${item.uptimePercent}%`}
                  />
                ) : (
                  <div
                    className="h-1.5 mt-2 rounded-full bg-muted/40"
                    role="presentation"
                    aria-hidden="true"
                  />
                )}
              </CardContent>
            </Card>
          ))}
        </div>
        )}
      </div>

      {/* Latency Trends — only render when at least one connection has live
          telemetry. An empty Card with bare axes is misleading. */}
      {chartConnections.length > 0 && (
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm">Latency Trends ({timeRange === '1h' ? 'Last Hour' : timeRange === '6h' ? 'Last 6 Hours' : timeRange === '24h' ? 'Last 24 Hours' : 'Last 7 Days'})</CardTitle>
              <CardDescription className="text-xs">Average response time across top connections</CardDescription>
            </div>
            <Select value={timeRange} onValueChange={v => setTimeRange(v as TimeRange)}>
              <SelectTrigger className="w-24 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1h">1 hour</SelectItem>
                <SelectItem value="6h">6 hours</SelectItem>
                <SelectItem value="24h">24 hours</SelectItem>
                <SelectItem value="7d">7 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
              <XAxis dataKey="time" tick={{ fontSize: 10 }} className="text-muted-foreground" />
              <YAxis tick={{ fontSize: 10 }} className="text-muted-foreground" unit="ms" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  fontSize: '11px',
                }}
              />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              {chartConnections.map((conn, i) => (
                <Line
                  key={conn.id}
                  type="monotone"
                  dataKey={conn.name}
                  stroke={LINE_COLORS[i % LINE_COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 3 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      )}

      {/* Recent Errors */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Recent Errors</CardTitle>
          <CardDescription className="text-xs">Latest warnings, errors, and critical events across all connections</CardDescription>
        </CardHeader>
        <CardContent>
          {errorLog.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">
              No recent errors logged.
            </p>
          ) : (
          <div className="space-y-2">
            {errorLog.map(entry => {
              const style = SEVERITY_STYLES[entry.severity];
              const SevIcon = style.icon;
              return (
                <div
                  key={entry.id}
                  className={cn('p-3 rounded-lg', style.bg)}
                >
                  <div className="flex items-start gap-2">
                    <SevIcon className={cn('h-4 w-4 mt-0.5 shrink-0', style.text)} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Badge variant="outline" className={cn('text-[9px]', style.text)}>
                          {entry.severity}
                        </Badge>
                        <Badge variant="outline" className="text-[9px]">
                          {entry.connectionType === 'mcp' ? 'MCP' : 'API'}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">{entry.connectionName}</span>
                        <span className="text-[10px] text-muted-foreground/60 ml-auto">
                          {new Date(entry.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-xs font-medium">{entry.message}</p>
                      {entry.details && (
                        <p className="text-[11px] text-muted-foreground mt-0.5">{entry.details}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── StatCard Component ─────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, trend, trendUp }: {
  icon: typeof Activity;
  label: string;
  value: string;
  trend?: string;
  trendUp?: boolean;
}) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center gap-2 mb-1">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-2xl font-bold">{value}</p>
          {trend && (
            <span className={cn(
              'text-[10px] font-medium',
              trendUp ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
            )}>
              {trend}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
